import * as THREE from 'three';

// Naming and calculations from Elva Golf python core.

// Backend provides EXTRINSIC XYZ Euler angles: R = Rz(z) · Ry(y) · Rx(x)
// For visualization, we need INTRINSIC interpretation where each rotation is around the current local axis after previous rotations

export function Rx(angle: number): THREE.Matrix3 {
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    return new THREE.Matrix3().set(1, 0, 0, 0, ca, -sa, 0, sa, ca);
}

export function Ry(angle: number): THREE.Matrix3 {
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    return new THREE.Matrix3().set(ca, 0, sa, 0, 1, 0, -sa, 0, ca);
}

export function Rz(angle: number): THREE.Matrix3 {
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    return new THREE.Matrix3().set(ca, -sa, 0, sa, ca, 0, 0, 0, 1);
}

// Build rotation matrix from extrinsic XYZ Euler angles
// R = Rz(z) * Ry(y) * Rx(x)
export function extrinsicXYZToMatrix(x: number, y: number, z: number): THREE.Matrix3 {
    const rx = Rx(x);
    const ry = Ry(y);
    const rz = Rz(z);
    // R = Rz * Ry * Rx
    return rz.clone().multiply(ry).multiply(rx);
}

//Extract intrinsic XYZ Euler angles from rotation matrix
export function matrixToIntrinsicXYZ(R: THREE.Matrix3): [number, number, number] {
    const elements = R.elements;

    const R02 = elements[6]; // R[0][2]
    const R12 = elements[7]; // R[1][2]
    const R22 = elements[8]; // R[2][2]
    const R01 = elements[3]; // R[0][1]
    const R00 = elements[0]; // R[0][0]

    // Clamp to avoid numerical issues at gimbal lock
    const sy = Math.max(-1, Math.min(1, R02));
    const y = Math.asin(sy);

    const x = Math.atan2(-R12, R22);
    const z = Math.atan2(-R01, R00);

    return [x, y, z];
}

export function extrinsicToIntrinsicXYZ(xe: number, ye: number, ze: number): [number, number, number] {
    const R = extrinsicXYZToMatrix(xe, ye, ze);
    return matrixToIntrinsicXYZ(R);
}

function applyMatrix3ToVector(matrix: THREE.Matrix3, vector: THREE.Vector3): THREE.Vector3 {
    return vector.clone().applyMatrix3(matrix);
}

export type IntrinsicVisualizationFrame = {
    cameraDirection: THREE.Vector3;
    referenceDirection: THREE.Vector3;
    actualDirection: THREE.Vector3;
    upDirection: THREE.Vector3;
};

export function getIntrinsicVisualizationFrame(
    eulerAngles: [number, number, number],
    axisIndex: 0 | 1 | 2,
): IntrinsicVisualizationFrame {
    const [bend, tilt, rotation] = eulerAngles;

    const globalX = new THREE.Vector3(1, 0, 0);
    const globalY = new THREE.Vector3(0, 1, 0);
    const globalZ = new THREE.Vector3(0, 0, 1);

    // Full rotation matrix: Rz * Ry * Rx (extrinsic XYZ order)
    const fullMatrix = Rz(rotation).multiply(Ry(tilt)).multiply(Rx(bend));

    switch (axisIndex) {
        case 0: {
            // Visualize X rotation
            // Reference: direction at 0° X rotation, with current Y and Z rotations
            // Rz * Ry (without Rx)
            const refMatrix = Rz(rotation).multiply(Ry(tilt));

            // Camera looks along local X axis (the rotation axis) to see the angle
            // Reference shows Y-axis (horizontal) with only Y+Z rotations
            // Actual shows Y-axis with all rotations including X
            return {
                cameraDirection: applyMatrix3ToVector(refMatrix, globalX),
                referenceDirection: applyMatrix3ToVector(refMatrix, globalY),
                actualDirection: applyMatrix3ToVector(fullMatrix, globalY),
                upDirection: applyMatrix3ToVector(refMatrix, globalZ),
            };
        }

        case 1: {
            // Visualize Y rotation
            // Reference: direction at 0° Y rotation, with current X and Z rotations
            // R_ref = Rz * Rx (without Ry)
            const refMatrix = Rz(rotation).multiply(Rx(bend));

            // Camera looks along local Y axis (the rotation axis) to see the angle
            // Reference shows Z-axis (vertical) with only X+Z rotations
            // Actual shows Z-axis with all rotations including Y
            return {
                cameraDirection: applyMatrix3ToVector(refMatrix, globalY),
                referenceDirection: applyMatrix3ToVector(refMatrix, globalZ),
                actualDirection: applyMatrix3ToVector(fullMatrix, globalZ),
                upDirection: applyMatrix3ToVector(refMatrix, globalZ),
            };
        }

        case 2: {
            // Visualize Z rotation
            // Reference: direction at 0° Z rotation, with current X and Y rotations
            // Ry * Rx (without Rz)
            const refMatrix = Ry(tilt).multiply(Rx(bend));

            // Camera looks down local Z axis (spine) to see shoulder turn
            // Reference shows Y-axis (lateral) with only X+Y rotations
            // Actual shows Y-axis with all rotations including Z
            const localZ = applyMatrix3ToVector(refMatrix, globalZ);
            const localX = applyMatrix3ToVector(refMatrix, globalX);

            return {
                cameraDirection: localZ.clone().negate(), // Look down from above
                referenceDirection: applyMatrix3ToVector(refMatrix, globalY),
                actualDirection: applyMatrix3ToVector(fullMatrix, globalY),
                upDirection: localX.clone().negate(), // Up is -local X (toward target)
            };
        }
    }
}

export function golfToWorldDirection(golfDir: THREE.Vector3): THREE.Vector3 {
    return new THREE.Vector3(golfDir.y, golfDir.z, golfDir.x);
}

export function directionToSpherical(direction: THREE.Vector3): {
    azimuthal: number;
    polar: number;
} {
    // Normalize the direction
    const dir = direction.clone().normalize();

    // Polar angle: angle from Y axis
    // cos(polar) = dir.y, so polar = acos(dir.y)
    const polar = Math.acos(Math.max(-1, Math.min(1, dir.y)));

    // Azimuthal angle: angle in XZ plane from positive Z
    // We need to handle the case where dir is along Y axis
    const azimuthal = Math.atan2(dir.x, dir.z);

    return { azimuthal, polar };
}

// Camera parameters for intrinsic angle visualization.
export type IntrinsicCameraParams = {
    // Camera position in world space
    position: THREE.Vector3;
    // Point the camera looks at in world space
    target: THREE.Vector3;
    // Camera up vector (controls roll to make reference horizontal)
    up: THREE.Vector3;
};

// Positions the camera to look down the local axis being visualized,
export function computeIntrinsicCameraParams(
    eulerAngles: [number, number, number],
    axisIndex: 0 | 1 | 2,
    targetPoint: THREE.Vector3,
    distance = 2,
): IntrinsicCameraParams {
    // Get the local coordinate frame from Euler angles
    const { cameraDirection, upDirection } = getIntrinsicVisualizationFrame(eulerAngles, axisIndex);

    // Transform from golf space to world space
    const worldLookDir = golfToWorldDirection(cameraDirection);
    const worldUpDir = golfToWorldDirection(upDirection);

    // Camera position: opposite to look direction, at specified distance
    const cameraOffset = worldLookDir.clone().negate().multiplyScalar(distance);
    const position = targetPoint.clone().add(cameraOffset);

    return {
        position,
        target: targetPoint.clone(),
        up: worldUpDir.normalize(),
    };
}

// Compute camera parameters to view a plane perpendicular (for joint angles).
export function computePlaneCameraParams(
    points: [THREE.Vector3, THREE.Vector3, THREE.Vector3],
    distance = 2,
): IntrinsicCameraParams {
    const [vertex, p1, p2] = points;

    // Compute vectors from vertex to the other two points
    const v1 = p1.clone().sub(vertex).normalize();
    const v2 = p2.clone().sub(vertex).normalize();

    // Plane normal = v1 × v2 (perpendicular to the plane)
    const normal = v1.clone().cross(v2).normalize();

    // If normal is zero (collinear points), fall back to a default view
    if (normal.lengthSq() < 0.001) {
        return {
            position: vertex.clone().add(new THREE.Vector3(0, 0, distance)),
            target: vertex.clone(),
            up: new THREE.Vector3(0, 1, 0),
        };
    }

    // Camera position: along the normal, at specified distance
    const position = vertex.clone().add(normal.clone().multiplyScalar(distance));

    // Up vector: use world Y if possible, otherwise derive from the plane
    let up = new THREE.Vector3(0, 1, 0);
    // If normal is nearly parallel to Y, use a different up
    if (Math.abs(normal.dot(up)) > 0.9) {
        up = new THREE.Vector3(1, 0, 0);
    }
    // Make up perpendicular to the view direction
    up = up
        .clone()
        .sub(normal.clone().multiplyScalar(up.dot(normal)))
        .normalize();

    return {
        position,
        target: vertex.clone(),
        up,
    };
}
